import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { collection, getDoc, getDocs, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Event } from '@/types';
import { formatCurrency } from '@/lib/utils';
import { DEFAULT_CREDIT_PACK_ROWS, type CreditPackRow } from '@masonic-tools/shared-types';
import { MasonicToolsOnboardingGuide } from '@/components/landing/MasonicToolsOnboardingGuide';
import {
  Calendar,
  CreditCard,
  CheckCircle,
  Award,
  Shield,
  ChevronRight,
  MapPin,
} from 'lucide-react';

interface PublicEvent extends Event {
  unitName: string;
}

/** Shared light-section layout (aligns with platform overview + investment blocks) */
const LANDING_SECTION_X = 'px-3 sm:px-4 md:px-5 lg:px-6';
const LANDING_KICKER =
  'mb-3 text-xs font-medium uppercase tracking-[0.14em] text-gold sm:mb-4 sm:text-sm sm:tracking-[0.18em]';
const LANDING_H2 = 'font-overview-serif text-2xl font-normal text-navy sm:text-3xl md:text-4xl';
const LANDING_LEAD = 'text-sm leading-relaxed text-slate-600 sm:text-base md:text-lg';

export default function LandingPage() {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState<PublicEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [creditPacks, setCreditPacks] = useState<CreditPackRow[]>(DEFAULT_CREDIT_PACK_ROWS);

  useEffect(() => {
    const loadPacks = async () => {
      try {
        const s = await getDoc(doc(db, 'systemSettings', 'platform'));
        const raw = s.data()?.creditPacks;
        let packs: CreditPackRow[];
        if (Array.isArray(raw) && raw.length > 0) {
          const cleaned = raw.filter(
            (p) =>
              p &&
              typeof p === 'object' &&
              typeof (p as CreditPackRow).id === 'string' &&
              typeof (p as CreditPackRow).credits === 'number' &&
              typeof (p as CreditPackRow).pricePence === 'number',
          ) as CreditPackRow[];
          packs = cleaned.length > 0 ? cleaned : [...DEFAULT_CREDIT_PACK_ROWS];
        } else {
          packs = [...DEFAULT_CREDIT_PACK_ROWS];
        }
        setCreditPacks(packs);
      } catch {
        setCreditPacks([...DEFAULT_CREDIT_PACK_ROWS]);
      }
    };
    loadPacks();
  }, []);

  useEffect(() => {
    const fetchPublicEvents = async () => {
      try {
        // Get all units first
        const unitsSnapshot = await getDocs(collection(db, 'units'));
        const publicEvents: PublicEvent[] = [];

        // For each unit, get public events
        for (const unitDoc of unitsSnapshot.docs) {
          const eventsSnapshot = await getDocs(
            collection(db, 'units', unitDoc.id, 'events')
          );

          eventsSnapshot.docs.forEach((eventDoc) => {
            const data = eventDoc.data();
            // Only include public events that are in the future and not deleted
            if (data.isPublic && data.date && data.eventStatus !== 'deleted') {
              const eventDate = data.date.toDate();
              if (eventDate > new Date()) {
                publicEvents.push({
                  id: eventDoc.id,
                  unitId: unitDoc.id,
                  unitName: unitDoc.data().name,
                  name: data.name,
                  ceremonyType: data.ceremonyType,
                  date: eventDate,
                  location: data.location,
                  venueId: data.venueId,
                  costPerPerson: data.costPerPerson,
                  questionnaire: data.questionnaire || [],
                  courses: data.courses || [],
                  currentAttendees: data.currentAttendees || 0,
                  maxCapacity: data.maxCapacity,
                  waitlistEnabled: data.waitlistEnabled || false,
                  waitlist: data.waitlist || [],
                  rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
                  paymentDeadline: data.paymentDeadline?.toDate() || (data.date?.toDate?.() || new Date()),
                  eventStatus: data.eventStatus || 'draft',
                  dresscode: data.dresscode,
                  customInstructions: data.customInstructions,
                  isPublic: data.isPublic,
                  requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
                  createdBy: data.createdBy,
                  createdAt: data.createdAt?.toDate() || new Date(),
                });
              }
            }
          });
        }

        // Sort by date (earliest first)
        publicEvents.sort((a, b) => a.date.getTime() - b.date.getTime());
        
        setUpcomingEvents(publicEvents);
      } catch (error) {
        console.error('Error fetching public events:', error);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchPublicEvents();
  }, []);

  const testimonials = [
    {
      quote: 'This has helped transform the way that my Lodge dines.',
      author: 'Dining Steward',
      unit: 'Universities Lodge',
    },
    {
      quote: 'Streamlined our entire dining process and made event management effortless for our Lodge.',
      author: 'Dining Steward',
      unit: 'Friendship & Justice Lodge',
    },
    {
      quote: 'A game-changer for our Chapter events. The platform handles everything with precision and elegance.',
      author: 'Dining Steward',
      unit: 'Colonnade Chapter',
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-gradient-to-b from-white via-slate-50 to-white">
      <Header />

      {/* Hero Section - Luxury Fine Dining Inspired */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy via-navy to-slate-900 px-4 py-12 sm:py-20 md:py-32 lg:py-40 text-white min-h-screen flex items-center">
        {/* Elegant Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(212,175,55,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(212,175,55,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(255,255,255,0.03),transparent_40%)]" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }} />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container relative mx-auto text-center w-full"
        >
          {/* Decorative Top Element */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 sm:mb-6 md:mb-8 flex items-center justify-center"
          >
            <div className="h-px w-12 sm:w-16 md:w-20 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
            <div className="mx-2 sm:mx-3 md:mx-4 w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-gold/30 rotate-45" />
            <div className="h-px w-12 sm:w-16 md:w-20 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto mb-6 sm:mb-8 md:mb-12 flex flex-col items-center justify-center"
          >
            <div className="mb-3 sm:mb-4 md:mb-6">
              <img 
                src="/logo.png" 
                alt="Dining Steward Logo" 
                className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 drop-shadow-2xl filter brightness-110"
              />
            </div>
            <h1 className="font-cursive text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl font-normal tracking-wide text-white leading-none px-2">
              Dining Steward
            </h1>
          </motion.div>
          
          {/* Decorative Middle Element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-4 sm:mb-6 md:mb-8 flex items-center justify-center"
          >
            <div className="h-px w-10 sm:w-12 md:w-16 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
            <div className="mx-3 sm:mx-4 md:mx-6 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gold/50 rotate-45" />
            <div className="h-px w-10 sm:w-12 md:w-16 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-6 sm:mb-8 md:mb-10"
          >
            <p className="mb-2 sm:mb-3 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light italic text-gold px-2">
              From labour to refreshment
            </p>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-slate-200 font-light px-2">
              with precision and elegance
            </p>
          </motion.div>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mx-auto mb-8 sm:mb-12 md:mb-16 max-w-4xl text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed text-slate-200 font-light px-2"
          >
            Modern event orchestration for distinguished Masonic units.
            Transform your festive boards with a full suite of event tools. There is
            no subscription: your unit purchases credits to cover confirmed RSVPs,
            and guest meal payments are separate.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-6 mb-8 sm:mb-10 md:mb-12 w-full px-2"
          >
            <Button 
              size="lg" 
              variant="gold" 
              onClick={() => navigate('/signup')}
              className="group w-full sm:w-auto px-8 sm:px-10 py-5 sm:py-6 md:py-7 text-base sm:text-lg md:text-xl shadow-2xl shadow-gold/25 transition-all hover:shadow-3xl hover:shadow-gold/35 hover:scale-105 font-medium sm:min-w-[280px]"
            >
              Begin Your Journey
              <ChevronRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto border-2 border-white/30 bg-white/10 px-8 sm:px-10 py-5 sm:py-6 md:py-7 text-base sm:text-lg md:text-xl text-white backdrop-blur-md transition-all hover:bg-white/20 hover:border-white/50 hover:scale-105 font-medium sm:min-w-[280px]"
            >
              Discover Features
            </Button>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm md:text-base text-slate-300 font-light px-2"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-gold flex-shrink-0" />
              <span>No monthly subscription</span>
            </div>
            <span className="text-gold text-lg sm:text-xl hidden sm:inline">•</span>
            <span>Unit credits for RSVPs</span>
            <span className="text-gold text-lg sm:text-xl hidden sm:inline">•</span>
            <span>Professional support</span>
          </motion.div>
        </motion.div>

        {/* Decorative Bottom Elements */}
        <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex items-center justify-center">
          <div className="h-px w-16 sm:w-20 md:w-24 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
          <div className="mx-2 sm:mx-3 md:mx-4 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gold/40 rotate-45" />
          <div className="h-px w-16 sm:w-20 md:w-24 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
      </section>

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section
          className={`border-t border-slate-200/80 bg-gradient-to-b from-white to-slate-50 py-12 sm:py-16 md:py-20 lg:py-24 ${LANDING_SECTION_X}`}
        >
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-8 text-center sm:mb-12 md:mb-16"
            >
              <p className={LANDING_KICKER}>On the calendar</p>
              <h2 className={LANDING_H2}>Upcoming events</h2>
              <p className={`mx-auto mt-2 max-w-2xl ${LANDING_LEAD} sm:mt-3`}>
                Join us for these upcoming dining events
              </p>
            </motion.div>

            {loadingEvents ? (
              <div className="flex justify-center items-center py-8 sm:py-12">
                <div className="h-10 w-10 sm:h-12 sm:w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
                {upcomingEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full border-slate-200 bg-white hover:border-gold hover:shadow-xl hover:shadow-gold/10 transition-all duration-300">
                      <CardHeader>
                        <CardTitle className="text-xl text-navy">{event.name}</CardTitle>
                        <CardDescription className="text-slate-600">
                          {event.unitName}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-start gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-navy">
                              {event.date.toLocaleDateString('en-GB', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                            <div className="text-slate-600">
                              {event.date.toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{event.location}</span>
                        </div>

                        {event.ceremonyType && (
                          <div className="flex items-start gap-2 text-sm">
                            <Award className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                            <span className="text-slate-600">{event.ceremonyType}</span>
                          </div>
                        )}

                        <div className="pt-4">
                          <Button
                            variant="gold"
                            className="w-full"
                            onClick={() => navigate('/login')}
                          >
                            RSVP to Event
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section
        className={`border-t border-slate-200/80 ${LANDING_SECTION_X} pt-10 pb-6 sm:pt-14 sm:pb-8 md:pt-16 md:pb-8 lg:pt-20 lg:pb-10`}
        aria-labelledby="app-features-heading"
      >
        <div className="mx-auto w-full max-w-3xl">
          <MasonicToolsOnboardingGuide />
        </div>
      </section>

      {/* Testimonials: tighter mobile section padding, readable line length, compact cards */}
      <section
        className={`border-y border-slate-200/80 bg-gradient-to-br from-slate-50 to-white py-8 sm:py-16 md:py-20 lg:py-24 ${LANDING_SECTION_X}`}
      >
        <div className="mx-auto w-full max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-6 text-center sm:mb-10 md:mb-16"
          >
            <p className={LANDING_KICKER}>From our units</p>
            <h2 className={LANDING_H2}>Trusted by Brethren</h2>
            <p className={`mx-auto max-w-2xl ${LANDING_LEAD} mt-2 sm:mt-3`}>
              Units across the country have elevated their event management
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 md:gap-7 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full min-h-0 border-slate-200 bg-white p-4 shadow-sm sm:p-6 md:p-7 lg:p-8">
                  <div className="mb-3 text-2xl text-gold sm:mb-4 sm:text-4xl">&ldquo;</div>
                  <p className="mb-4 text-[15px] leading-relaxed italic text-slate-700 sm:mb-5 md:mb-6 sm:text-base">
                    {testimonial.quote}
                  </p>
                  <div className="border-t border-slate-200 pt-3 sm:pt-4">
                    <div className="text-sm font-medium text-navy sm:text-base">{testimonial.author}</div>
                    <div className="text-xs text-slate-500 sm:text-sm">{testimonial.unit}</div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing: less vertical padding on small screens, readable copy, feature list at sm+ text */}
      <section className={`py-8 sm:py-16 md:py-20 lg:py-24 ${LANDING_SECTION_X}`}>
        <div className="mx-auto w-full max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-6 text-center sm:mb-10 md:mb-16"
          >
            <p className={LANDING_KICKER}>Pricing and credits</p>
            <h2 className={LANDING_H2}>Transparent investment</h2>
            <p className={`mx-auto max-w-2xl ${LANDING_LEAD} mt-2 sm:mt-3`}>
              No monthly fee. Your unit tops up with credit packs, uses credits when guests confirm places, and handles
              meal tickets and any card fees on top.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full"
          >
            <Card className="relative overflow-hidden border-2 border-gold bg-gradient-to-br from-white to-slate-50 shadow-2xl">
              <div className="absolute right-0 top-0 h-32 w-32 bg-gold opacity-5" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

              <div className="p-4 text-center sm:p-8 md:p-10">
                <div className="mb-2 flex justify-center">
                  <span className="inline-flex max-w-[min(100%,18rem)] items-center justify-center rounded-full bg-gold/10 px-2.5 py-1.5 text-center text-[9px] font-medium uppercase leading-snug tracking-wide text-gold sm:max-w-none sm:px-4 sm:text-sm sm:leading-normal sm:tracking-normal">
                    No monthly subscription
                  </span>
                </div>
                <h3 className="mb-2 text-xl font-semibold text-navy sm:text-2xl md:text-3xl">Platform and RSVPs</h3>
                <p className="mb-5 text-sm leading-relaxed text-slate-600 sm:mb-6 sm:mb-8 sm:text-base">
                  The software has no standing charge. Units buy Masonic Tools credits in packs; each event can require
                  a number of credits per confirmed RSVP (commonly one).
                </p>

                <div className="mb-5 sm:mb-6 md:mb-8">
                  <div className="mb-1 text-4xl font-bold leading-none text-navy sm:mb-2 sm:text-5xl md:text-6xl lg:text-7xl">£0</div>
                  <div className="text-xs text-slate-500 sm:text-base">Fixed monthly or annual platform fee</div>
                </div>

                <div className="mb-5 text-left sm:mb-6 sm:mb-8">
                  <div className="mb-2.5 border-b border-slate-200 pb-2 text-center text-sm font-medium text-navy sm:mb-3 sm:text-base">
                    Credit packs
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-4">
                    {[...creditPacks]
                      .sort((a, b) => a.credits - b.credits)
                      .map((pack) => (
                        <div
                          key={pack.id}
                          className="rounded-lg border border-navy/15 bg-navy/5 p-3 text-center sm:p-4"
                        >
                          <div className="text-base font-semibold text-navy sm:text-lg">
                            {pack.label?.trim() || `${pack.credits} credits`}
                          </div>
                          <div className="mt-0.5 text-xl font-bold text-navy sm:mt-1 sm:text-2xl">
                            {formatCurrency(pack.pricePence / 100)}
                          </div>
                          {pack.description?.trim() ? (
                            <p className="mt-1.5 text-left text-xs leading-snug text-slate-600 sm:mt-2 sm:text-center">
                              {pack.description.trim()}
                            </p>
                          ) : null}
                        </div>
                      ))}
                  </div>
                </div>

                <div className="mb-5 rounded-xl border border-navy/20 bg-navy/5 p-3.5 text-left sm:mb-6 sm:mb-8 sm:p-5 md:p-6">
                  <div className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700 sm:gap-2">
                    <CreditCard className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
                    <span>
                      <span className="font-medium text-navy">Meal and guest payments. </span>
                      Ticket prices are set per event. Card and other online methods can include a small platform
                      transaction fee; amounts are configured for each payment type in system settings and apply when
                      guests pay online.
                    </span>
                  </div>
                </div>

                <div className="mb-5 text-left sm:mb-6 sm:mb-8">
                  <div className="mb-2.5 border-b border-slate-200 pb-2 text-center text-sm font-medium text-navy sm:mb-3 sm:mb-4 sm:text-base">
                    Complete feature suite
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-4">
                    {[
                      'Unlimited events',
                      'Unlimited guests',
                      'Payment processing',
                      'Table planner',
                      'RSVP management',
                      'Email notifications',
                      'Analytics & reports',
                      'Priority support',
                    ].map((feature, index) => (
                      <div key={index} className="flex min-h-0 items-center gap-2.5 sm:min-h-0 sm:gap-2">
                        <CheckCircle className="h-4 w-4 flex-shrink-0 text-gold sm:h-5 sm:w-5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 border-t border-slate-200 pt-3.5 sm:pt-4 md:pt-6">
                  <p className="text-left text-[13px] leading-normal text-slate-600 sm:text-sm md:text-center">
                    Example: if an event uses one credit per RSVP and a guest pays a £50 ticket online, the unit has
                    used one credit; the guest pays the ticket total plus any configured card fee.
                  </p>
                  <p className="text-sm font-medium text-navy">Credits and meal payments are shown clearly in the app</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className={`bg-gradient-to-br from-navy via-navy to-slate-900 py-12 text-white sm:py-16 md:py-20 lg:py-24 ${LANDING_SECTION_X}`}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="container mx-auto max-w-3xl text-center"
        >
          <h2 className="font-overview-serif text-2xl font-normal sm:text-3xl md:text-4xl">Elevate your events</h2>
          <p className="mx-auto mb-8 max-w-2xl text-base text-slate-300 sm:mb-10 sm:text-lg">
            Join the units that have discovered a more refined approach to event management
          </p>
          <Button 
            size="lg" 
            variant="gold"
            onClick={() => navigate('/signup')}
            className="group w-full sm:w-auto px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg shadow-xl shadow-gold/20 transition-all hover:shadow-2xl hover:shadow-gold/30"
          >
            Get started
            <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:translate-x-1" />
          </Button>
          <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-slate-400 px-2">
            No standing subscription. Buy credit packs as your unit needs them.
          </p>
        </motion.div>
      </section>
    </div>
  );
}

